import React, { useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { LanguageProvider, useLanguage } from "@/contexts/LanguageContext";
import LanguageRouter from "@/components/LanguageRouter";
import GoogleAnalytics from "@/components/GoogleAnalytics";
import Index from "./pages/Index";
import ContactUs from "./pages/ContactUs";
import BecomePartner from "./pages/BecomePartner";
import AboutUs from "./pages/AboutUs";
import OtherServices from "./pages/OtherServices";
import Sustainwize from "./pages/Sustainwize";
import SustainwizeContactUs from "./pages/SustainwizeContactUs";
import SustainwizeDownloadMaterial from "./pages/SustainwizeDownloadMaterial";
import Decarbonwize from "./pages/Decarbonwize";
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";
import BlogAuthor from "./pages/BlogAuthor";
import BlogTag from "./pages/BlogTag";
import TermsOfService from "./pages/TermsOfService";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import NotFound from "./pages/NotFound";

const queryClient = new QueryClient();

// Component to scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

// Component to handle hash scrolling
const ScrollToHash = () => {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      // Small delay to ensure the page has loaded
      setTimeout(() => {
        const element = document.getElementById(location.hash.slice(1));
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 100);
    }
  }, [location]);

  return null;
};

// Define all routes as a component to avoid repetition
const AppRoutes = () => (
  <Routes>
    {/* Thai routes (default, no prefix) */}
    <Route path="/" element={<Index />} />
    <Route path="/about-us" element={<AboutUs />} />
    <Route path="/contact-us" element={<ContactUs />} />
    <Route path="/become-partner" element={<BecomePartner />} />
    <Route path="/other-services" element={<OtherServices />} />
    <Route path="/sustainwize" element={<Sustainwize />} />
    <Route path="/sustainwize/contact-us" element={<SustainwizeContactUs />} />
    <Route path="/sustainwize/download-material" element={<SustainwizeDownloadMaterial />} />
    <Route path="/decarbonwize" element={<Decarbonwize />} />
    <Route path="/blog" element={<Blog />} />
    <Route path="/blog/tag" element={<Navigate to="/blog" replace />} />
    <Route path="/blog/author" element={<Navigate to="/blog" replace />} />
    <Route path="/blog/:slug" element={<BlogPost />} />
    <Route path="/blog/author/:slug" element={<BlogAuthor />} />
    <Route path="/blog/tag/:slug" element={<BlogTag />} />
    <Route path="/terms-of-service" element={<TermsOfService />} />
    <Route path="/privacy-policy" element={<PrivacyPolicy />} />

    {/* English routes (with /en prefix) */}
    <Route path="/en" element={<Index />} />
    <Route path="/en/about-us" element={<AboutUs />} />
    <Route path="/en/contact-us" element={<ContactUs />} />
    <Route path="/en/become-partner" element={<BecomePartner />} />
    <Route path="/en/other-services" element={<OtherServices />} />
    <Route path="/en/sustainwize" element={<Sustainwize />} />
    <Route path="/en/sustainwize/contact-us" element={<SustainwizeContactUs />} />
    <Route path="/en/sustainwize/download-material" element={<SustainwizeDownloadMaterial />} />
    <Route path="/en/decarbonwize" element={<Decarbonwize />} />
    <Route path="/en/blog" element={<Blog />} />
    <Route path="/en/blog/tag" element={<Navigate to="/en/blog" replace />} />
    <Route path="/en/blog/author" element={<Navigate to="/en/blog" replace />} />
    <Route path="/en/blog/:slug" element={<BlogPost />} />
    <Route path="/en/blog/author/:slug" element={<BlogAuthor />} />
    <Route path="/en/blog/tag/:slug" element={<BlogTag />} />
    <Route path="/en/terms-of-service" element={<TermsOfService />} />
    <Route path="/en/privacy-policy" element={<PrivacyPolicy />} />

    {/* Redirect /th/* to /* (canonical Thai URLs) */}
    <Route path="/th" element={<Navigate to="/" replace />} />
    <Route path="/th/*" element={<Navigate to="/" replace />} />

    {/* 404 */}
    <Route path="*" element={<NotFound />} />
  </Routes>
);

const AppContent = () => {
  const { language } = useLanguage();

  // Prevent right-click on images
  useEffect(() => {
    const handleContextMenu = (e: MouseEvent) => {
      if ((e.target as HTMLElement).tagName === "IMG") {
        e.preventDefault();
      }
    };

    document.addEventListener("contextmenu", handleContextMenu);
    return () => document.removeEventListener("contextmenu", handleContextMenu);
  }, []);
  
  return (
    <div className={language === "th" ? "font-thai" : ""}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <GoogleAnalytics />
          <LanguageRouter>
            <ScrollToTop />
            <ScrollToHash />
            <AppRoutes />
          </LanguageRouter>
        </BrowserRouter>
      </TooltipProvider>
    </div>
  );
};

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <LanguageProvider>
        <AppContent />
      </LanguageProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
